import { useState } from 'react'

import { isDemoMode, type CustomAgentResultItem } from '@axilon-apps/domains-ontology-ui-state'

import { SchemaAwareResultView } from '../custom-agents/SchemaAwareResultView'
import { ActionItemCard } from '../insights'
import { EventCorrectionButton, EventCorrectionDetail } from './EventCorrection'
import { SummaryReportSection, SummaryReportSections } from './summary-layout'
import {
  SUMMARY_CONSUMED_KEYS,
  leftoverRecord,
  mapActionItems,
  mapSummary,
  mapSummaryMetrics,
  mapSummaryNotableEventFromItem,
  mapSummaryNotableEvents
} from './mappers'
import type { MappedSummaryEvent } from './mappers'

interface Props {
  result: Record<string, unknown>
  eventItems?: readonly CustomAgentResultItem[]
  /**
   * When true, "This was incorrect" arms a pending reject and the reason is
   * typed on the Agent tab — the inline composer is not shown.
   */
  reasonOnAgentTab?: boolean
  onSetEventCorrect?: (eventId: string, correct: boolean, reason?: string) => void | Promise<void>
}

interface SummaryPresenterEvent extends MappedSummaryEvent {
  id?: string
  approval_status?: CustomAgentResultItem['approval_status']
  denialNotes?: string
}

export function SummaryPresenter({
  result,
  eventItems = [],
  reasonOnAgentTab = false,
  onSetEventCorrect
}: Props) {
  const [correctingEventId, setCorrectingEventId] = useState<string | null>(null)
  const [reasonDraft, setReasonDraft] = useState('')
  const narrative = mapSummary(result.narrative) ?? ''
  const metrics = mapSummaryMetrics(result.metrics)
  const shreddedEvents = eventItems
    .filter((item) => item.type === 'notable_events')
    .sort((left, right) => left.position - right.position)
    .flatMap((item) => mapSummaryNotableEventFromItem(item) ?? [])
  const notableEvents: SummaryPresenterEvent[] =
    shreddedEvents.length > 0 ? shreddedEvents : mapSummaryNotableEvents(result.notable_events)
  const actions = mapActionItems(result.action_items)
  const leftover = leftoverRecord(result, SUMMARY_CONSUMED_KEYS)
  const closeComposer = () => {
    setCorrectingEventId(null)
    setReasonDraft('')
  }

  return (
    <div className="flex flex-col gap-7">
      <SummaryReportSections
        narrative={narrative}
        metrics={metrics}
        notableEvents={notableEvents}
        renderEventAction={(event) => {
          const eventId = event.id
          if (!eventId || !onSetEventCorrect) return null
          return (
            <EventCorrectionButton
              event={event}
              isComposing={correctingEventId === eventId}
              onUndo={() => {
                closeComposer()
                void onSetEventCorrect(eventId, true)
              }}
              onToggleComposer={() => {
                if (reasonOnAgentTab) {
                  void onSetEventCorrect(eventId, false)
                  return
                }
                if (correctingEventId === eventId) {
                  closeComposer()
                  return
                }
                setCorrectingEventId(eventId)
                setReasonDraft(
                  isDemoMode()
                    ? 'This never happened — the line was down for a planned changeover, not a fault.'
                    : ''
                )
              }}
            />
          )
        }}
        renderEventFooter={(event) => {
          const eventId = event.id
          if (!eventId || !onSetEventCorrect || reasonOnAgentTab) return null
          return (
            <EventCorrectionDetail
              event={event}
              isComposing={correctingEventId === eventId}
              draft={reasonDraft}
              onDraftChange={setReasonDraft}
              onCancel={closeComposer}
              onConfirm={() => {
                const reason = reasonDraft.trim()
                if (!reason) return
                void onSetEventCorrect(eventId, false, reason)
                closeComposer()
              }}
            />
          )
        }}
      >
        {actions.length > 0 ? (
          <SummaryReportSection label="Potential action items">
            <ol className="m-0 flex list-none flex-col gap-3 p-0">
              {actions.map((action, index) => (
                <li key={`${action.title}-${index}`}>
                  <ActionItemCard
                    index={index + 1}
                    title={action.title}
                    description={action.description}
                    priority={action.priority}
                    components={action.components}
                    showChevron={false}
                  />
                </li>
              ))}
            </ol>
          </SummaryReportSection>
        ) : null}

        {leftover ? (
          <SummaryReportSection label="Other fields">
            <SchemaAwareResultView value={leftover} />
          </SummaryReportSection>
        ) : null}
      </SummaryReportSections>
    </div>
  )
}
